import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, ConfirmDeleteModal, SideDrawer } from '@/components';
import {
  getCustomerTypes,
  createCustomerType,
  updateCustomerType,
  deleteCustomerType,
} from '../api';
import editIcon from '../assets/editIcon.png';
import trashIcon from '../assets/trashIcon.png';
import { PermissionGuard } from '@/common/components';
import { PERMISSIONS } from '@/common/config/permissions';

export function CustomerTypesModal({ isOpen, onClose, currentCompanyId }) {
  const queryClient = useQueryClient();
  const [newTypeName, setNewTypeName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [typeToDelete, setTypeToDelete] = useState(null);

  const { data: customerTypesData, isLoading } = useQuery({
    queryKey: ['customer-types', currentCompanyId],
    queryFn: () => getCustomerTypes(currentCompanyId),
    enabled: !!currentCompanyId && isOpen,
  });

  const customerTypes = customerTypesData || [];

  const createMutation = useMutation({
    mutationFn: (payload) =>
      createCustomerType({ companyId: currentCompanyId, payload }),
    onSuccess: () => {
      toast.success('Customer type created!');
      setNewTypeName('');
      queryClient.invalidateQueries({
        queryKey: ['customer-types', currentCompanyId],
      });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          'Failed to create type.',
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ typeId, payload }) =>
      updateCustomerType({ companyId: currentCompanyId, typeId, payload }),
    onSuccess: () => {
      toast.success('Customer type updated!');
      setEditingId(null);
      setNewTypeName('');
      queryClient.invalidateQueries({
        queryKey: ['customer-types', currentCompanyId],
      });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          'Failed to update type.',
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (typeId) =>
      deleteCustomerType({ companyId: currentCompanyId, typeId }),
    onSuccess: () => {
      toast.success('Customer type deleted!');
      queryClient.invalidateQueries({
        queryKey: ['customer-types', currentCompanyId],
      });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          'Failed to delete type.',
      );
    },
  });

  const handleCreate = () => {
    if (!newTypeName.trim()) return;
    createMutation.mutate({ name: newTypeName.trim() });
  };

  const handleUpdate = (id) => {
    if (!newTypeName.trim()) return;
    updateMutation.mutate({
      typeId: id,
      payload: { name: newTypeName.trim() },
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNewTypeName('');
  };

  return (
    <>
      <SideDrawer
        className="contacts-panel"
        isOpen={isOpen}
        onClose={onClose}
        width="md"
        showClose={false}
        zIndexClass="z-[55]"
        headerClassName="!px-6 !py-5"
        bodyClassName="p-6 flex flex-col min-h-0 !overflow-hidden"
        header={
          <div className="flex items-center justify-between w-full gap-3">
            <h2 className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
              Manage Customer Types
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
      >
        <div className="flex flex-col h-full min-h-0">
          {/* Add New */}
          <div className="bg-[#F5F5F5] rounded-[12px] p-4 flex flex-col gap-2 mb-6 shrink-0">
            <label className="text-[14px] font-medium text-text-secondary">
              Customer Type Name
            </label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="New customer type name..."
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (editingId) handleUpdate(editingId);
                    else handleCreate();
                  }
                  if (e.key === 'Escape' && editingId) handleCancelEdit();
                }}
                className="flex-1 h-11 px-4 rounded-xl bg-white border border-border-default text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none focus:border-primary transition-all"
              />
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="text-sm font-semibold text-text-muted hover:text-text-primary transition-colors shrink-0"
                >
                  Cancel
                </button>
              )}
              <Button
                variant="primary"
                onClick={() =>
                  editingId ? handleUpdate(editingId) : handleCreate()
                }
                disabled={
                  !newTypeName.trim() ||
                  createMutation.isPending ||
                  updateMutation.isPending
                }
                className="h-11 px-4 shrink-0"
              >
                {editingId
                  ? updateMutation.isPending
                    ? 'Saving...'
                    : 'Update'
                  : createMutation.isPending
                    ? 'Adding...'
                    : 'Add'}
              </Button>
            </div>
          </div>

          {/* List */}
          {isLoading ? (
            <div className="py-8 text-center text-text-muted text-sm">
              Loading types...
            </div>
          ) : customerTypes.length === 0 ? (
            <div className="py-8 text-center text-text-muted text-sm bg-layer-1-page rounded-xl border border-border-default">
              No customer types found.
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5] rounded-[12px] p-4 flex flex-col gap-3">
              {customerTypes.map((type) => (
                <div
                  key={type.id}
                  className={`flex items-center justify-between p-3 rounded-xl bg-white hover:bg-input-background transition-colors ${
                    editingId === type.id ? 'ring-1 ring-primary' : ''
                  }`}
                >
                  <span className="text-sm font-medium text-text-primary">
                    {type.name}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <PermissionGuard permission={PERMISSIONS.CONTACTS.UPDATE}>
                      <button
                        onClick={() => {
                          setEditingId(type.id);
                          setNewTypeName(type.name);
                        }}
                        className="p-1.5 rounded-lg hover:bg-white/10 text-text-muted hover:text-text-primary transition-all"
                        title="Edit"
                      >
                        <img
                          src={editIcon}
                          alt=""
                          className="w-4 h-4 object-contain"
                        />
                      </button>
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.CONTACTS.DELETE}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTypeToDelete(type);
                        }}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-text-muted hover:text-red-500 transition-all disabled:opacity-50"
                        title="Delete"
                      >
                        <img
                          src={trashIcon}
                          alt=""
                          className="w-4 h-4 object-contain"
                        />
                      </button>
                    </PermissionGuard>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SideDrawer>

      <ConfirmDeleteModal
        isOpen={!!typeToDelete}
        title="Delete Customer Type"
        description={`This will permanently delete '${typeToDelete?.name}'.`}
        isPending={deleteMutation.isPending}
        onCancel={() => setTypeToDelete(null)}
        onConfirm={() => {
          deleteMutation.mutate(typeToDelete.id, {
            onSuccess: () => setTypeToDelete(null),
          });
        }}
      />
    </>
  );
}
